import type { LlmLogSelectDto, LlmLogStatus, LlmLogType } from '@@types/api.types';
import { Badge } from '@components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDownWideNarrowIcon, ArrowUpDown, ArrowUpNarrowWideIcon } from 'lucide-react';
import React from 'react';
import { LLM_LOG_TYPE_LABELS } from '../../../../../utils/constants';

const statusVariantMap: Record<LlmLogStatus, 'default' | 'success' | 'destructive' | 'pending' | 'warning'> = {
  aborted: 'warning',
  completed: 'success',
  failed: 'destructive',
  pending: 'pending',
};

const formatDateTime = (value: string) => {
  return format(new Date(value), 'PPP p');
};

const formatDuration = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  if (value < 1000) {
    return `${Math.round(value)} ms`;
  }
  const seconds = value / 1000;
  return `${seconds.toFixed(seconds < 10 ? 2 : 1)} s`;
};

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat().format(value);
};

const formatType = (value?: LlmLogType) => {
  if (!value) return '—';
  return LLM_LOG_TYPE_LABELS[value];
};

const toTitle = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type AdminLlmTableProps = {
  logs: LlmLogSelectDto[];
  selectedLogId?: string | null;
  sorting: SortingState;
  onRowClick: (logId: string) => void;
  onSortingChange: (sorting: SortingState) => void;
};

export const AdminLlmTable: React.FC<AdminLlmTableProps> = (props) => {
  const { logs, selectedLogId, sorting, onRowClick, onSortingChange } = props;

  const columns = React.useMemo<ColumnDef<LlmLogSelectDto>[]>(
    () => [
      {
        id: 'userCompany',
        header: 'User / Company',
        accessorFn: (row) => {
          return `${row.user?.profile?.fullName || ''} ${row.user?.companies?.[0]?.name || ''}`.trim();
        },
        sortingFn: 'text',
        cell: ({ row }) => {
          return (
            <div className="flex flex-col">
              <span className="text-sm text-gray-900">{row.original.user?.profile?.fullName || '—'}</span>
              {row.original.user?.companies?.[0]?.name && (
                <span className="text-xs text-gray-500">{row.original.user?.companies?.[0]?.name}</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'type',
        header: 'Type',
        accessorFn: (row) => row.type ?? '',
        sortingFn: 'text',
        cell: ({ row }) => formatType(row.original.type),
      },
      {
        id: 'model',
        header: 'Model',
        accessorFn: (row) => row.metadata?.requestedModel ?? row.metadata?.actualModel ?? '',
        sortingFn: 'text',
        cell: ({ row }) => row.original.metadata?.requestedModel ?? row.original.metadata?.actualModel ?? '—',
      },
      {
        id: 'version',
        header: 'Version',
        accessorFn: (row) => row.metadata?.templateVersion ?? -1,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const version = row.original.metadata?.templateVersion;

          return version === null || version === undefined ? '—' : `v${version}`;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Requested At',
        cell: ({ getValue }) => formatDateTime(getValue<string>()),
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        sortingFn: 'text',
        cell: ({ row }) => (
          <Badge
            size="xs"
            variant={statusVariantMap[row.original.status] ?? 'default'}
          >
            {toTitle(row.original.status)}
          </Badge>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    columns,
    data: logs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    state: { sorting },
  });

  return (
    <div className="overflow-hidden rounded-xl border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                if (header.isPlaceholder) {
                  return <TableHead key={header.id} />;
                }

                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <TableHead key={header.id}>
                    {canSort ? (
                      <button
                        className="flex items-center gap-1 text-sm font-semibold text-gray-600"
                        onClick={header.column.getToggleSortingHandler()}
                        type="button"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                        {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                        {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                data-state={row.original.id === selectedLogId ? 'selected' : undefined}
                onClick={() => onRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className="h-24 text-center text-sm text-gray-500"
                colSpan={columns.length}
              >
                No interactions recorded yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
